import { ChevronRight, Info, Pause, Play, Radio, Trash2 } from 'lucide-react';
import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useSearchParams } from '@/lib/scopedParams';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { api, type LogLine } from '../../api';
import { Badge, type Tone } from '../../components/Badge';
import { Field, Section } from '../../components/blocks';
import { DataError, OpsReasonBar } from '../../components/DataError';
import { CorrelationLink, TraceLink } from '../../components/Ids';
import { PageHeader, useDocumentTitle } from '../../components/PageHeader';
import { EmptyState, Loading } from '../../components/States';
import { TimeRangePicker } from '../../components/TimeRangePicker';
import { formatDateTime, formatDateTimeMs } from '../../lib/format';
import { MAX_LOG_LINES, MAX_QUERY_CHARS, namesBusinessObjects, rangeFromParams, rangeToParams, resolveRange, type TimeRange } from '../../lib/opsQuery';
import { useApi } from '../../lib/useApi';
import { CodeChips } from './shared';

const TAIL_INTERVAL_MS = 5000;
const TAIL_WINDOW_S = 30;
const TAIL_MAX_LINES = 1000;

const LEVEL_TONE: Record<string, Tone> = { error: 'crit', warn: 'warn', warning: 'warn', info: 'info', debug: 'neutral', trace: 'neutral' };
const LEVEL_BORDER: Partial<Record<Tone, string>> = { crit: 'border-l-critical', warn: 'border-l-warning', info: 'border-l-info' };
const ANY = '__any';

/** Spec §114 filters, turned into LogQL. */
interface Filters {
  service: string;
  level: string;
  registry: string;
  operation: string;
  trace_id: string;
  request_id: string;
  correlation_id: string;
  error_code: string;
  text: string;
}

const EMPTY_FILTERS: Filters = { service: '', level: '', registry: '', operation: '', trace_id: '', request_id: '', correlation_id: '', error_code: '', text: '' };

const esc = (v: string) => v.replace(/\\/g, '\\\\').replace(/"/g, '\\"');

function buildQuery(f: Filters): string {
  const selector = [f.service ? `service="${esc(f.service)}"` : 'service=~".+"', f.level ? `level="${esc(f.level)}"` : ''].filter(Boolean).join(', ');
  const fields = (['registry', 'operation', 'trace_id', 'request_id', 'correlation_id', 'error_code'] as const)
    .filter((k) => f[k].trim())
    .map((k) => ` | ${k}="${esc(f[k].trim())}"`)
    .join('');
  return `{${selector}}${fields ? ` | json${fields}` : ''}${f.text.trim() ? ` |= "${esc(f.text.trim())}"` : ''}`;
}

export const lineKey = (l: LogLine) => `${l.timestamp}|${l.labels.service ?? ''}|${l.line}`;

/** Fields shown as chips on a line when it carries them (registry exchanges and EPP commands). */
const KEY_FIELDS = ['operation', 'object', 'cltrid', 'svtrid', 'result_code', 'latency_ms'] as const;

/** A field of a line: from its JSON (as scrubbed), else from the labels a `| json` query extracts. */
function logField(l: LogLine, json: Record<string, unknown> | null, key: string): string | null {
  const v = json?.[key] ?? l.labels[key];
  return v === undefined || v === null || v === '' || typeof v === 'object' ? null : String(v);
}

export function prettyLine(line: string): { message: string; json: Record<string, unknown> | null } {
  try {
    const v = JSON.parse(line) as unknown;
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      const o = v as Record<string, unknown>;
      return { message: typeof o.msg === 'string' ? o.msg : typeof o.message === 'string' ? o.message : line, json: o };
    }
  } catch {
    // plain text line
  }
  return { message: line, json: null };
}

function contextLink(l: LogLine): string {
  const t = new Date(l.timestamp).getTime();
  const p = new URLSearchParams({ q: `{service="${l.labels.service ?? ''}"}` });
  rangeToParams({ kind: 'custom', start: new Date(t - 60_000).toISOString(), end: new Date(t + 60_000).toISOString() }, p);
  return `/ops/logs?${p.toString()}`;
}

/** One log line; `correlation={false}` on the correlation page, which already is that link. */
export function LogRow({ l, correlation = true }: { l: LogLine; correlation?: boolean }) {
  const { t } = useTranslation('ops');
  const { message, json } = prettyLine(l.line);
  const level = (l.labels.level ?? (typeof json?.level === 'string' ? json.level : '')).toLowerCase();
  const tone = LEVEL_TONE[level] ?? 'neutral';
  const domain = typeof json?.domain === 'string' ? json.domain : null;
  const keyFields = KEY_FIELDS.flatMap((k) => {
    const v = logField(l, json, k);
    return v === null ? [] : [`${k}=${v}`];
  });
  return (
    <li className={cn('space-y-1.5 border-l-2 border-l-transparent px-5 py-3', LEVEL_BORDER[tone])}>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <time dateTime={l.timestamp} title={l.timestamp} className="font-mono text-xs text-muted-foreground">
          {formatDateTimeMs(l.timestamp)}
        </time>
        {level ? (
          <Badge tone={tone} icon={false}>
            {level}
          </Badge>
        ) : null}
        <code className="rounded bg-muted px-1.5 py-0.5 text-xs">{l.labels.service ?? '—'}</code>
        <span className="min-w-0 basis-full text-sm break-words sm:basis-auto sm:flex-1">{message}</span>
      </div>
      {keyFields.length ? <CodeChips items={keyFields} /> : null}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
        {l.links.trace_id ? <TraceLink id={l.links.trace_id} label={t('logs.row.trace')} /> : null}
        {correlation && l.links.correlation_id ? <CorrelationLink id={l.links.correlation_id} label={t('logs.row.correlation')} /> : null}
        {domain ? (
          <Link to={`/domains/${encodeURIComponent(domain)}`} className="text-primary hover:underline">
            {domain}
          </Link>
        ) : null}
        <Link to={contextLink(l)} className="text-primary hover:underline">
          {t('logs.row.context')}
        </Link>
        <details className="group basis-full">
          <summary className="inline-flex cursor-pointer items-center gap-1 text-muted-foreground select-none hover:text-foreground">
            <ChevronRight className="size-3 transition-transform group-open:rotate-90" aria-hidden="true" />
            {t('logs.row.raw')}
          </summary>
          <pre className="mt-2 max-h-80 overflow-auto rounded-md border bg-muted/40 p-3 font-mono text-xs">{json ? JSON.stringify(json, null, 2) : l.line}</pre>
        </details>
      </div>
    </li>
  );
}

function FilterBuilder({ onBuild }: { onBuild: (q: string) => void }) {
  const { t } = useTranslation('ops');
  const [f, setF] = useState<Filters>(EMPTY_FILTERS);
  const set = (k: keyof Filters) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  const field = (k: keyof Filters, label: string, placeholder = '') => (
    <Field label={label}>
      <Input type="text" value={f[k]} onChange={set(k)} placeholder={placeholder} autoComplete="off" spellCheck={false} className="h-8" />
    </Field>
  );
  return (
    <details className="group mt-5 border-t pt-4">
      <summary className="inline-flex cursor-pointer items-center gap-1.5 text-sm font-medium select-none">
        <ChevronRight className="size-4 text-muted-foreground transition-transform group-open:rotate-90" aria-hidden="true" />
        {t('logs.builder.title')}
      </summary>
      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {field('service', t('logs.builder.service'), 'api')}
        <Field label={t('logs.builder.level')}>
          <Select value={f.level || ANY} onValueChange={(v) => setF({ ...f, level: v === ANY ? '' : v })}>
            <SelectTrigger size="sm" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY}>{t('logs.builder.any')}</SelectItem>
              {['error', 'warn', 'info', 'debug'].map((l) => (
                <SelectItem key={l} value={l}>
                  {l}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        {field('registry', t('logs.builder.registry'), 'VNNIC')}
        {field('operation', t('logs.builder.operation'), 'domain_create')}
        {field('trace_id', t('logs.builder.traceId'))}
        {field('request_id', t('logs.builder.requestId'))}
        {field('correlation_id', t('logs.builder.correlationId'))}
        {field('error_code', t('logs.builder.errorCode'))}
        {field('text', t('logs.builder.text'))}
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <Button type="button" variant="outline" size="sm" onClick={() => onBuild(buildQuery(f))}>
          {t('logs.builder.use')}
        </Button>
        <code className="min-w-0 rounded bg-muted px-2 py-1 text-xs break-all">{buildQuery(f)}</code>
      </div>
    </details>
  );
}

/** Polls `/ops/v1/logs/tail` while live; lines are merged newest first and capped. */
function LiveTail({ query }: { query: string }) {
  const { t } = useTranslation('ops');
  const { t: tc } = useTranslation();
  const [lines, setLines] = useState<LogLine[]>([]);
  const [error, setError] = useState<unknown>(null);
  const [paused, setPaused] = useState(false);
  const seen = useRef(new Set<string>());

  useEffect(() => {
    if (paused || error) return undefined;
    let controller: AbortController | null = null;
    let timer: number | undefined;
    let stopped = false;
    const poll = async () => {
      controller = new AbortController();
      try {
        const res = await api.tailLogs(query, TAIL_WINDOW_S, { signal: controller.signal });
        if (stopped) return;
        const fresh = res.items.filter((l) => !seen.current.has(lineKey(l)));
        for (const l of fresh) seen.current.add(lineKey(l));
        if (fresh.length) {
          setLines((prev) => {
            const merged = [...fresh, ...prev].sort((a, b) => (a.timestamp < b.timestamp ? 1 : -1));
            return merged.slice(0, TAIL_MAX_LINES);
          });
        }
        timer = window.setTimeout(poll, TAIL_INTERVAL_MS);
      } catch (err) {
        if (!stopped && !(err instanceof DOMException && err.name === 'AbortError')) setError(err);
      }
    };
    void poll();
    return () => {
      stopped = true;
      controller?.abort();
      window.clearTimeout(timer);
    };
  }, [query, paused, error]);

  const running = !paused && !error;
  return (
    <Section
      flush
      title={
        <span className="flex items-center gap-2">
          <Radio className={cn('size-4', running ? 'text-success' : 'text-muted-foreground')} aria-hidden="true" />
          {t('logs.liveTail')}
          <Badge tone={running ? 'ok' : 'neutral'} icon={false}>
            {running ? t('logs.tail.running') : t('logs.tail.paused')}
          </Badge>
        </span>
      }
      description={<span role="status">{t('logs.tail.status', { interval: TAIL_INTERVAL_MS / 1000, window: TAIL_WINDOW_S, count: lines.length, max: TAIL_MAX_LINES })}</span>}
      actions={
        <>
          <Button type="button" variant="outline" size="sm" onClick={() => setPaused((p) => !p)} disabled={Boolean(error)}>
            {paused ? <Play /> : <Pause />} {paused ? t('logs.tail.resume') : t('logs.tail.pause')}
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              setLines([]);
              seen.current.clear();
            }}
          >
            <Trash2 /> {tc('action.clear')}
          </Button>
        </>
      }
    >
      {error ? (
        <div className="px-5 pb-5">
          <DataError error={error} onRetry={() => setError(null)} title={t('logs.tail.stopped')} />
        </div>
      ) : null}
      {lines.length === 0 && !error ? (
        <div className="px-5 pb-5">
          <EmptyState title={t('logs.tail.waiting')} />
        </div>
      ) : null}
      {lines.length ? (
        <ol className="divide-y border-t" aria-live="off">
          {lines.map((l) => (
            <LogRow key={lineKey(l)} l={l} />
          ))}
        </ol>
      ) : null}
    </Section>
  );
}

/** `/ops/logs`: LogQL search, filters, live tail and links to traces, correlations and domains (spec §114). */
export function LogsPage() {
  const { t } = useTranslation('ops');
  useDocumentTitle(t('logs.title'));
  const id = useId();
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const live = params.get('live') === '1';
  const range = rangeFromParams(params, '15m');
  const limit = Math.min(MAX_LOG_LINES, Math.max(1, Number(params.get('limit') ?? 500) || 500));
  const [draft, setDraft] = useState(q);
  const [nonce, setNonce] = useState(0);
  const key = params.toString();

  // Keep the editor in step with the URL (links from other pages, back/forward).
  const [syncedFrom, setSyncedFrom] = useState(q);
  if (q !== syncedFrom) {
    setSyncedFrom(q);
    setDraft(q);
  }

  const result = useApi(
    async (signal) => {
      if (!q || live) return null;
      const { start, end } = resolveRange(range);
      return api.queryLogs({ query: q, start: start.toISOString(), end: end.toISOString(), limit }, { signal });
    },
    [key, nonce],
  );

  const apply = (next: { q?: string; range?: TimeRange; live?: boolean; limit?: number }) => {
    const p = new URLSearchParams(params);
    if (next.q !== undefined) p.set('q', next.q);
    if (next.range) rangeToParams(next.range, p);
    if (next.live !== undefined) {
      if (next.live) p.set('live', '1');
      else p.delete('live');
    }
    if (next.limit !== undefined) p.set('limit', String(next.limit));
    setParams(p);
    setNonce((n) => n + 1);
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (draft.trim()) apply({ q: draft.trim() });
  };

  const summary = (() => {
    if (result.status !== 'success' || !result.data) return null;
    const n = result.data.items.length;
    const { start, end } = resolveRange(range);
    return [
      t('logs.result', { count: n }),
      n >= limit ? t('logs.limitReached', { limit }) : null,
      result.data.scrubbed ? t('logs.scrubbed') : null,
      t('logs.range', { start: formatDateTime(start.toISOString()), end: formatDateTime(end.toISOString()) }),
    ]
      .filter(Boolean)
      .join(' · ');
  })();

  return (
    <>
      <PageHeader title={t('logs.title')} subtitle={t('logs.subtitle')} />
      <div className="space-y-6">
        <OpsReasonBar />
        <Section>
          <form className="space-y-4" onSubmit={submit}>
            <div className="grid gap-1.5">
              <label htmlFor={`${id}-q`} className="text-xs font-medium text-muted-foreground">
                {t('logs.logql')}
              </label>
              <Textarea
                id={`${id}-q`}
                className="min-h-16 font-mono text-sm"
                rows={2}
                value={draft}
                maxLength={MAX_QUERY_CHARS}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) submit(e);
                }}
                spellCheck={false}
                placeholder='{service="api", level="error"}'
              />
              <p className="text-xs text-muted-foreground">{t('shared.runHint')}</p>
              {namesBusinessObjects(draft) ? (
                <p className="flex items-center gap-1.5 text-xs text-warning">
                  <Info className="size-3.5" aria-hidden="true" /> {t('shared.reasonHint')}
                </p>
              ) : null}
            </div>
            <div className="flex flex-wrap items-center gap-x-5 gap-y-3">
              <Button type="submit" size="sm" disabled={!draft.trim()}>
                <Play /> {t('shared.run')}
              </Button>
              <div className="flex items-center gap-2">
                <Switch id={`${id}-live`} checked={live} onCheckedChange={(v) => apply({ q: draft.trim() || q, live: v })} disabled={!draft.trim() && !q} />
                <label htmlFor={`${id}-live`} className="text-sm">
                  {t('logs.liveTail')}
                </label>
              </div>
              {!live ? (
                <div className="flex items-center gap-2">
                  <span id={`${id}-limit`} className="text-sm text-muted-foreground">
                    {t('logs.limit')}
                  </span>
                  <Select value={String(limit)} onValueChange={(v) => apply({ limit: Number(v) })}>
                    <SelectTrigger size="sm" className="w-24" aria-labelledby={`${id}-limit`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[100, 500, 1000, 5000].map((n) => (
                        <SelectItem key={n} value={String(n)}>
                          {n}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : null}
            </div>
            {!live ? <TimeRangePicker key={key} value={range} onChange={(r) => apply({ range: r })} /> : null}
          </form>
          <FilterBuilder
            onBuild={(built) => {
              setDraft(built);
              apply({ q: built });
            }}
          />
        </Section>

        {live && q ? <LiveTail key={`${q}/${nonce}`} query={q} /> : null}

        {!live ? (
          <Section title={q ? t('logs.resultTitle') : undefined} description={summary ? <span role="status">{summary}</span> : undefined} flush>
            {!q ? (
              <div className="px-5 pb-5">
                <EmptyState title={t('logs.emptyTitle')}>{t('logs.emptyBody', { example: '{service="api"}' })}</EmptyState>
              </div>
            ) : null}
            {q && result.status === 'loading' ? (
              <div className="px-5">
                <Loading label={t('logs.searching')} />
              </div>
            ) : null}
            {q && result.status === 'error' ? (
              <div className="px-5 pb-5">
                <DataError error={result.error} onRetry={() => setNonce((n) => n + 1)} title={t('logs.failed')} />
              </div>
            ) : null}
            {result.status === 'success' && result.data ? (
              result.data.items.length === 0 ? (
                <div className="px-5 pb-5">
                  <EmptyState title={t('logs.noLines')} />
                </div>
              ) : (
                <ol className="divide-y border-t">
                  {result.data.items.map((l, i) => (
                    <LogRow key={`${lineKey(l)}#${i}`} l={l} />
                  ))}
                </ol>
              )
            ) : null}
          </Section>
        ) : null}
      </div>
    </>
  );
}
